import { serverBus } from '@/main'
import { traccar } from '@/api/traccar-api'
import * as event from '../../events'
import Vue from 'vue'
import * as notifications from '../../utils/notifications'
import { chunkArray } from '@/utils/utils'

const state = {
  dataLoaded: false,
  devicesLoaded: false,
  notificationPermissionsLoaded: false,
  historyMode: false,
  mosaicDeviceId: null,
  events: [],
  stiLoaded: false,
  isPlaying: false,
  trips: [],
  stops: [],
  portrait: true,
  loading: false,
  loadingEvents: false,
  summary: [],
  totalDistance: 0,
  search: '',
  routeFetched: false,
  initialized: false,
  percentage: 0
}

const mutations = {
  SET_NOTIFICATION_PERMISSIONS_LOADED(state, value) {
    state.notificationPermissionsLoaded = value
  },
  SET_PERCENTAGE(state, value) {
    state.percentage = value
  },
  SET_INITIALIZED(state, value) {
    state.initialized = value
  },
  SET_ROUTE_FETCHED(state, value) {
    state.routeFetched = value
  },
  SET_SEARCH(state, value) {
    state.search = value
  },
  SET_TOTAL_DISTANCE(state, value) {
    state.totalDistance = value
  },
  SET_LOADING(state, value) {
    state.loading = value
  },
  SET_LOADING_EVENTS(state, value) {
    state.loadingEvents = value
  },
  SET_PORTRAIT(state, value) {
    state.portrait = value
  },
  SET_TRIPS(state, trips) {
    state.trips = trips
  },
  SET_STOPS(state, stops) {
    state.stops = stops
  },
  TOGGLE_PLAY(state) {
    state.isPlaying = !state.isPlaying
  },
  SET_PLAYING(state, value) {
    state.isPlaying = value
  },
  SET_EVENTS(state, events) {
    Vue.$log.debug('SET_EVENTS:', events.length)
    state.events = events
    serverBus.$emit(event.eventsLoaded)
  },
  ADD_EVENTS(state, events) {
    state.events = events.concat(state.events)
  },
  SET_DATA_LOADED(state, value = true) {
    state.dataLoaded = value
  },
  SET_DEVICES_LOADED(state, value = true) {
    state.devicesLoaded = value
  },
  TOGGLE_HISTORY_MODE: (state) => {
    state.historyMode = !state.historyMode
    if (state.historyMode) { state.routeFetched = false }
    state.trips.splice(0)
    serverBus.$emit(event.showRoutesChanged)
    setTimeout(() => serverBus.$emit(event.mapShow), 500)
  },
  SET_MOSAIC_DEVICE(state, deviceId) {
    state.mosaicDeviceId = deviceId
    setTimeout(() => serverBus.$emit(event.mapShow), 500)
  }
}

const actions = {
  setLoading({ commit }, loading) {
    commit('SET_LOADING', loading)
  },
  setPortrait({ commit }, value) {
    commit('SET_PORTRAIT', value)
  },
  setTrips({ commit }, trips) {
    commit('SET_TRIPS', trips)
  },
  setStops({ commit }, stops) {
    commit('SET_STOPS', stops)
  },
  setPlaying({ commit }, value) {
    commit('SET_PLAYING', value)
  },
  togglePlaying({ commit }) {
    commit('TOGGLE_PLAY')
  },
  toggleHistoryMode(context) {
    context.commit('TOGGLE_HISTORY_MODE')
    context.dispatch('setPlaying', false)
  },
  toggleMosaic({ commit, state }, deviceId) {
    commit('SET_MOSAIC_DEVICE', state.mosaicDeviceId === deviceId ? null : deviceId)
  },
  setDataLoaded({ commit }) {
    commit('SET_DATA_LOADED')
  },
  setDevicesLoaded({ commit }) {
    commit('SET_DEVICES_LOADED')
  },
  async loadNotificationPermissions({ commit, dispatch }) {
    commit('user/SET_LOAD_NOTIFICATIONS_BY_DEVICE', true, { root: true })
    return dispatch('user/fetchAlerts', null, { root: true })
  },
  async fetchEvents({ commit, rootGetters, state, dispatch }, { start, end, types }) {
    // [alerts-perf] timing logs to find where time / CPU goes when opening Alerts (ticket #200)
    const tStart = performance.now()
    let t = tStart
    if (!state.notificationPermissionsLoaded) {
      await dispatch('loadNotificationPermissions')
      commit('SET_NOTIFICATION_PERMISSIONS_LOADED', true)
      console.log('[alerts-perf] loadNotificationPermissions', Math.round(performance.now() - t), 'ms')
    }
    const eventsReceived = []
    if (types.length) {
      t = performance.now()
      const r = await traccar.report_events(
        start,
        end,
        rootGetters.devices.map(d => d.id),
        types
      )
      const total = r.reduce((n, response) => n + response.data.length, 0)
      console.log('[alerts-perf] report_events', Math.round(performance.now() - t), 'ms,', total, 'events,',
        rootGetters.devices.length, 'devices, types', types)

      t = performance.now()
      const timings = { match: 0, warn: 0, accept: 0 }
      let slowest = { ms: 0 }
      // Drop alarm events whose subtype no alarm alert of the user listens to
      // (most of the alarm events on accounts with many sensors).
      const alarms = new Set(rootGetters.alerts
        .filter(a => a && a.notification.type === 'alarm')
        .flatMap(a => a.notification.attributes.alarms || []))
      const candidates = r.flatMap(response => response.data)
        .filter(e => e.type !== 'alarm' || alarms.has(e.attributes && e.attributes.alarm))
      // notificationAccepts may fetch a position per event, so check events in
      // parallel batches instead of awaiting them one by one.
      for (const chunk of chunkArray(candidates, 20)) {
        const accepted = await Promise.all(chunk.map(async e => {
          const te = performance.now()
          const result = await notifications.filterEvent(e, rootGetters.alerts, timings)
          const ms = performance.now() - te
          if (ms > slowest.ms) {
            slowest = { ms: Math.round(ms), id: e.id, type: e.type, alarm: e.attributes && e.attributes.alarm }
          }
          return result
        }))
        chunk.forEach((e, i) => accepted[i] && eventsReceived.push(e))
      }
      console.log('[alerts-perf] filterEvent', Math.round(performance.now() - t), 'ms total,',
        eventsReceived.length, 'accepted /', candidates.length, 'candidates /', total, '| match', Math.round(timings.match),
        'ms, cant-find warn', Math.round(timings.warn), 'ms, notificationAccepts', Math.round(timings.accept),
        'ms | slowest', slowest, '|', rootGetters.alerts.length, 'alerts')
    }
    t = performance.now()
    const events = notifications.convertEvents(eventsReceived, false)
    console.log('[alerts-perf] convertEvents', Math.round(performance.now() - t), 'ms,', events.length, 'events')
    t = performance.now()
    commit('SET_EVENTS', events)
    await Vue.nextTick()
    console.log('[alerts-perf] SET_EVENTS + render', Math.round(performance.now() - t), 'ms')
    console.log('[alerts-perf] fetchEvents total', Math.round(performance.now() - tStart), 'ms')
  },
  addEvents({ commit }, events) {
    Vue.$log.debug('Commit addevent')
    commit('ADD_EVENTS', events)
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions
}

